import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Empty } from "antd";
import { PieChartOutlined } from "@ant-design/icons";
import { PieChart, Pie, Cell, Tooltip } from "recharts";

import {
  Card,
  CardTitle,
  CardCaption,
  OutcomeDonutWrap,
  OutcomeDonut,
  DonutCenter,
  DonutTotal,
  DonutCenterLabel,
  OutcomeLegend,
  OutcomeRow,
  OutcomeLabel,
  OutcomeCount,
  ToneDot
} from "./dashboardStyles";
import { outcomeLabel, outcomeColor, percent, NON_FINAL_OUTCOME_CODES, FAILURE_OUTCOME_CODE } from "./dashboardHelpers";
import { GRAY_200, GRAY_800, WHITE } from "../../constant/colors";

// Donut geometry — a 24px ring, matching the tile's 150px box. `innerRadius`
// leaves the hole for the centered total overlay (DonutCenter).
const SIZE = 150;
const RING = 24;
const OUTER_RADIUS = SIZE / 2;
const INNER_RADIUS = OUTER_RADIUS - RING;

/**
 * Hover tooltip content — names the slice under the cursor + its share. This is
 * why the donut is a real `recharts` chart rather than hand-rolled SVG arcs
 * (which had no hover affordance).
 */
const OutcomeSliceTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const row = payload[0]?.payload;
  if (!row) return null;
  return (
    <div
      data-testid="agentic-dashboard-outcomes-tooltip"
      style={{
        background: WHITE,
        border: `1px solid ${GRAY_200}`,
        borderRadius: 6,
        padding: "4px 10px",
        fontSize: 12,
        color: GRAY_800,
        boxShadow: "0 2px 8px rgba(0, 0, 0, 0.12)"
      }}
    >
      <span
        style={{
          display: "inline-block",
          width: 8,
          height: 8,
          borderRadius: "50%",
          background: row.color,
          marginRight: 6
        }}
      />
      {row.label} — <strong>{row.count.toLocaleString()}</strong> ({row.pct}%)
    </div>
  );
};

/**
 * Outcome breakdown — a donut of the SUCCESS workflows (runs that completed
 * without error) in the range, with a legend. Intermediate codes (`in_flight`,
 * the consent pause reason) AND `failure` are filtered out, so the donut answers
 * "of the runs that didn't error, how did they end" and the shares sum to 100%
 * over those. The error count lives in its own "Failed" KPI tile, not a donut
 * slice. Each outcome gets its OWN color (a modern categorical palette via
 * `outcomeColor`). Rendered with `recharts` so each slice has a hover tooltip;
 * the legend shows the rounded share. Range-scoped.
 */
const DashboardOutcomes = ({ stats }) => {
  const { t } = useTranslation();

  const { rows, total } = useMemo(() => {
    const byOutcome = stats?.byOutcome ?? {};
    // Success workflows only — drop in-flight (no terminal yet) and `failure`
    // (the error count is surfaced as its own KPI tile, not a donut slice).
    const successEntries = Object.entries(byOutcome).filter(
      ([code]) => !NON_FINAL_OUTCOME_CODES.has(code) && code !== FAILURE_OUTCOME_CODE
    );
    const tot = successEntries.reduce((sum, [, count]) => sum + count, 0);
    const mapped = successEntries
      .map(([code, count]) => ({
        code,
        count,
        pct: percent(count, tot),
        label: outcomeLabel(code, t),
        color: outcomeColor(code)
      }))
      .sort((a, b) => b.count - a.count);
    return { rows: mapped, total: tot };
  }, [stats, t]);

  return (
    <Card data-testid="agentic-dashboard-outcomes">
      <CardTitle>
        <PieChartOutlined />
        {t("agentic_dashboard.outcomes.title")}
      </CardTitle>
      <CardCaption>{t("agentic_dashboard.outcomes.caption")}</CardCaption>

      {total === 0 ? (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t("agentic_dashboard.outcomes.empty")} />
      ) : (
        <OutcomeDonutWrap>
          <OutcomeDonut data-testid="agentic-dashboard-outcomes-donut">
            <PieChart width={SIZE} height={SIZE}>
              <Pie
                data={rows}
                dataKey="count"
                nameKey="label"
                cx="50%"
                cy="50%"
                innerRadius={INNER_RADIUS}
                outerRadius={OUTER_RADIUS}
                // Start at 12 o'clock, go clockwise (matches the previous layout).
                startAngle={90}
                endAngle={-270}
                stroke="none"
                isAnimationActive={false}
              >
                {rows.map((row) => (
                  <Cell key={row.code} fill={row.color} />
                ))}
              </Pie>
              <Tooltip content={<OutcomeSliceTooltip />} />
            </PieChart>
            <DonutCenter>
              <DonutTotal>{total.toLocaleString()}</DonutTotal>
              <DonutCenterLabel>{t("agentic_dashboard.outcomes.center_label")}</DonutCenterLabel>
            </DonutCenter>
          </OutcomeDonut>

          <OutcomeLegend>
            {rows.map((row) => (
              <OutcomeRow key={row.code} data-testid={`agentic-dashboard-outcome-${row.code}`}>
                <OutcomeLabel>
                  <ToneDot $color={row.color} />
                  {row.label}
                </OutcomeLabel>
                <OutcomeCount>
                  {row.count.toLocaleString()}
                  <span>{row.pct}%</span>
                </OutcomeCount>
              </OutcomeRow>
            ))}
          </OutcomeLegend>
        </OutcomeDonutWrap>
      )}
    </Card>
  );
};

export { DashboardOutcomes };
